/**
 * Whether this user has answered the new-dashboard prompt — one key, one value.
 *
 * `'yes'` once they open v2 from anywhere (the navbar banner or the launch
 * prompt), `'later'` if they dismiss the prompt. Either answer stops the prompt
 * announcing a launch again; only `'yes'` means they actually went.
 *
 * One entry rather than an opt-in flag plus a dismissed flag: the two can only
 * ever be read together, and separate keys allow states that mean nothing
 * ("dismissed but also opted in").
 *
 * Distinct from `v2BannerState`, which is the banner's own hide toggle and is
 * reversible from the profile menu — this is not.
 *
 * `localStorage`, so it is per-browser: someone who opted in on their laptop is
 * prompted again on a new machine or after clearing site data. Accepted for
 * launch — the alternative is a persisted user preference, which is a backend
 * change.
 */

const KEY = 'frontend-v2-opt-in:v1';

type OptInValue = 'yes' | 'later';

function read(): OptInValue | null {
  try {
    return localStorage.getItem(KEY) as OptInValue | null;
  } catch {
    // Private mode / storage disabled. Report 'later' so a prompt that cannot
    // remember being answered doesn't reappear on every page load.
    return 'later';
  }
}

function write(value: OptInValue): void {
  try {
    localStorage.setItem(KEY, value);
  } catch {
    /* ignore */
  }
}

/** True once the user has answered either way — the prompt has had its turn. */
export function hasAnsweredV2Prompt(): boolean {
  return read() !== null;
}

/** True when the user has actually been to v2 — the signal for sending them
 *  straight there on their next visit, rather than back to this dashboard. */
export function hasOptedInToV2(): boolean {
  return read() === 'yes';
}

/** Called when the user comes back from v2 deliberately. Downgrades the opt-in
 *  so they are not sent there again — leaving is an answer too. */
export function setV2OptedOut(): void {
  write('later');
}

export function setOptedInToV2(): void {
  write('yes');
}

export function setV2PromptDismissed(): void {
  // Never downgrade a 'yes': the banner stays available to someone who has
  // already been to v2, and clicking it must not look like a fresh dismissal.
  if (read() !== 'yes') write('later');
}
